import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService, type AvailableLangs } from '@jsverse/transloco';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { filter, from, map, switchMap, take } from 'rxjs';
import { BiddingApiService } from '../features/bidding/services/bidding-api.service';
import { LogoComponent } from '../shared/logo.component';
import { PageHeaderService } from './services/page-header.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [LogoComponent, TranslocoPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './header.component.css',
  templateUrl: './header.component.html',
})
export class HeaderComponent {
  protected readonly pageHeader = inject(PageHeaderService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly oktaAuth = inject(OKTA_AUTH);
  private readonly oktaAuthState = inject(OktaAuthStateService);
  private readonly router = inject(Router);
  private readonly biddingApi = inject(BiddingApiService);

  protected readonly userMenuOpen = signal(false);

  protected readonly userFullName = toSignal(
    this.oktaAuthState.authState$.pipe(
      filter((authState) => authState.isAuthenticated === true),
      switchMap(() => from(this.oktaAuth.getUser())),
      map(
        (claims) =>
          (claims?.name as string) ??
          (claims?.preferred_username as string) ??
          (claims?.email as string) ??
          '',
      ),
      takeUntilDestroyed(this.destroyRef),
    ),
    { initialValue: '' },
  );

  protected readonly isAuthPage = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects.startsWith('/auth')),
      takeUntilDestroyed(this.destroyRef),
    ),
    { initialValue: this.router.url.startsWith('/auth') },
  );

  protected readonly availableLangs = signal(
    (this.transloco.getAvailableLangs() as AvailableLangs).map((lang) =>
      typeof lang === 'string' ? lang : lang.id,
    ),
  );

  protected readonly activeLang = toSignal(
    this.transloco.langChanges$.pipe(takeUntilDestroyed(this.destroyRef)),
    { initialValue: this.transloco.getActiveLang() },
  );

  protected toggleUserMenu(): void {
    this.userMenuOpen.update((open) => !open);
  }

  protected closeUserMenu(): void {
    this.userMenuOpen.set(false);
  }

  protected setLang(lang: string): void {
    this.transloco.setActiveLang(lang);
  }

  protected downloadGuide(): void {
    this.biddingApi
      .downloadGuide()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (!response.body) {
          return;
        }

        const link = document.createElement('a');
        const objectUrl = URL.createObjectURL(response.body);
        link.href = objectUrl;
        link.download = this.getGuideFileName(response.headers.get('Content-Disposition'));
        link.click();
        URL.revokeObjectURL(objectUrl);
      });
  }

  protected logout(): void {
    this.closeUserMenu();
    const postLogoutRedirectUri = window.location.origin + '/auth/login';
    void this.oktaAuth.signOut({ postLogoutRedirectUri });
  }

  private getGuideFileName(contentDisposition: string | null): string {
    const match = contentDisposition?.match(/filename\*?=(?:UTF-8''|\")?([^;\"]+)/i);
    return match?.[1]?.trim() || 'user-guide.pdf';
  }
}
